<template>
    <div>
        <div class="flex justify-content-end gap-2 py-4" v-if="!inPrevious">
            <Button label="Previous Classes" :class="isInstitute ? 'yellow-button-small' : 'teal-button-small'" @click="$emit('view-class', {})" />
        </div>

        <div class="flex flex-column">
            <span class="Paragraph-Medium">Subjects</span>
            <div class="flex flex-wrap align-items-start justify-content-start gap-3 py-5">
                <span v-for="module in displayedModules" :key="module"
                    class="flex items-center justify-center px-2 py-2 rounded Caption subjects">
                    {{ cleanupLables(module) }}
                </span>
            </div>
        </div>

        <div class="flex flex-column">
            <span class="Paragraph-Medium mb-4">{{ inPrevious ? 'Previous' : 'Upcoming' }} classes</span>

            <div class="grid-container" v-if="classes && classes.length > 0">
                <div v-for="(item, index) in classes" :key="index">
                    <Card :class="['Card p-4 h-full', { 'cancelled-card': isCancelledClass(item) }]">
                        <template #content>
                            <div class="flex flex-column gap-2 w-full">
                                <div class="class-card-header">
                                    <Button v-if="canCancelClass(item)" icon="pi pi-times-circle" class="p-button-text p-button-sm p-0 cancel-class-btn" title="Cancel Class" @click.stop="confirmCancelClass(item.classInstanceId)" />
                                    <span v-else class="cancel-icon-placeholder"></span>
                                    <Badge v-if="isCancelledClass(item)" severity="danger">Cancelled</Badge>
                                    <Badge v-else :severity="item.isPaid ? 'success' : 'warn'">{{ item.isPaid ? 'Paid' : 'Payment Pending' }}</Badge>
                                </div>
                                <span
                                    class="Paragraph-Medium mb-2 white-space-nowrap overflow-hidden text-overflow-ellipsis"
                                    :title="item.subject">
                                    {{cleanupLables(item.subject)}}
                                </span>
                                <span class="Caption">{{ cleanupLables(item.classType) }}</span>
                                <span class="Caption" v-if="item.classLocation">At {{ cleanupLables(item.classLocation) }}</span>
                                <span class="Caption flex gap-3">
                                    <span>{{ item.date }}</span>
                                    <span>{{ item.start }} - {{ item.end }}</span>
                                </span>
                            </div>
                        </template>
                        <template #footer>
                            <div class="flex justify-content-center mt-3">
                                <Button label="View" @click="$emit('view-info', { id: item.classInstanceId })"
                                    :disabled="isCancelledClass(item)"
                                    :class="[isInstitute ? 'yellow-button-small' : 'teal-button-small', 'view w-8rem']" />
                            </div>
                        </template>
                    </Card>
                </div>
            </div>

            <span v-else class="Caption text-gray-700 text-center">
                You don’t have any {{ inPrevious ? 'previous' : 'upcoming' }} classes with this student.
            </span>
        </div>
    </div>
</template>

<script setup>
import { cleanupLables } from "@/utils/cleanups";
import Button from "primevue/button";
import Card from "primevue/card";
import { defineProps, defineEmits, computed } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { Badge } from "primevue";
import { useConfirm } from "primevue/useconfirm";
import { notifyError, notifySuccess } from "@/utils/notification";
import { cancelClassInstance } from "@/api/tutor";

const props = defineProps({
    classes: Array,
    modules: Array,
    inPrevious: Boolean
});

const emit = defineEmits(["view-class", "view-info", "refresh-classes"]);
const confirm = useConfirm();

const authStore = useAuthStore();
const isInstitute = computed(() => authStore.user?.role === 'institute');

const isCancelledClass = (item) => {
    const status = (item?.classStatus ?? item?.status ?? '').toString().toLowerCase();
    return status === 'cancelled' || status === 'canceled';
};

const isUpcomingClass = (item) => {
    const status = (item?.classStatus ?? item?.status ?? '').toString().toLowerCase();
    return status === 'upcoming';
};

const canCancelClass = (item) => !isCancelledClass(item) && isUpcomingClass(item);

const confirmCancelClass = (classInstanceId) => {
    confirm.require({
        message: 'Are you sure you want to cancel this pending class? This action cannot be undone.',
        header: 'Cancel Class',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, Cancel Class',
        rejectLabel: 'Keep Class',
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-text p-button-secondary',
        accept: async () => {
            try {
                await cancelClassInstance(classInstanceId, { refundNote: '' });
                notifySuccess('Success', 'This class has been cancelled.');
                emit('refresh-classes');
            } catch (error) {
                notifyError('Error', error?.response?.data?.message || 'Failed to cancel class. Please try again.');
            }
        },
    });
};

const displayedModules = computed(() => {
    if (!props.inPrevious) {
        return props.modules;
    } else {
        if (props.classes && props.classes.length > 0) {
            const subjects = props.classes.map(item => item.subject);
            return [...new Set(subjects)]; // Returns unique values
        }
        return [];
    }
});
</script>

<style scoped>
.grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
    gap: 3rem;
}

.Card {
    border-radius: 4px;
    box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.16);
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.subjects {
    border: 1px solid var(--gray-700) !important;
    border-radius: 50px;
    background-color: transparent;
}

.view {
    padding: 8px 16px 8px 16px;
    height: 30px;
    font-size: 0.75rem;
}

.class-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.cancel-icon-placeholder {
    width: 1.5rem;
    display: inline-block;
}

.cancel-class-btn {
    color: var(--red-500) !important;
    justify-content: flex-start !important;
}

.view:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.cancelled-card {
    opacity: 0.72;
    background: #fff7f7;
    border: 1px solid rgba(220, 38, 38, 0.25);
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

.teal-button-small {
    height: 30px;
    font-size: 0.75rem;
}

.yellow-button-small {
    height: 30px;
    font-size: 0.75rem;
}
</style>